import React from 'react';

import { StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { Box } from 'components/ui/box';
import { Typography } from 'components/ui/typography';
import { logUserErrorMessage } from 'hooks/log-user-error-message';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { handleFatalErrorReload } from 'utils/fatal-error-handler';

import IconError from './icon';
import { useReload } from './use-reload';

function ErrorBoundary() {
  const localeTitle = t('errorBoundaryTitle');
  const localeBody = t('errorBoundaryBody');
  const onReload = useReload(handleFatalErrorReload);
  const isDesktopWebBreakpoint = useIsDesktopWebBreakpoint();

  useEffectOnce(() => {
    logEvent(CustomEventNames.APP_ERROR, EventTypes.Other);
    logUserErrorMessage({ message: localeTitle });
  });

  return (
    <Box testID="error-boundary" style={styles.container}>
      <Box style={styles.inner}>
        <IconError />
        <Typography
          variant="headerOne"
          style={[styles.header, isDesktopWebBreakpoint && styles.desktopHeader]}
        >
          {localeTitle}
        </Typography>
        <Typography variant="copyTwo" fontSize={14} style={styles.subheading}>
          {localeBody}
        </Typography>

        <ActionButton onPress={onReload} testID="error-boundary-reload-btn">
          {t('reload')}
        </ActionButton>
      </Box>
    </Box>
  );
}

export default ErrorBoundary;

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: primitive.$greyFive,
    bottom: 0,
    flex: 1,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  header: {
    marginVertical: spacing.$5,
    maxWidth: '75%',
    textAlign: 'center',
    textTransform: getDesignToken('text-transform-headlines'),
  },
  desktopHeader: {
    maxWidth: 500,
  },
  inner: {
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 380,
    padding: spacing.$8,
  },
  subheading: {
    marginBottom: spacing.$4,
    textAlign: 'center',
  },
});
